import { Tabs, type TabsTabProps } from "@mantine/core";
import { useContext } from "react";
import { useMatch, useNavigate, useResolvedPath } from "react-router";
import { useMissingData } from "../../../hooks/useMissingData";
import { TabWithHighlightAndError } from "../../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import {
  OBJECT_NAMES_TO_OBJECT_URL_NAMES,
  OBJECT_URL_NAMES_TO_OBJECT_NAMES,
  ObjectName,
  ObjectUrlName
} from "../../../utils/enums";
import { TABLE_IDS } from "../../ComponentListContainer/ComponentList/ComponentTable";
import { ComponentListContainer } from "../../ComponentListContainer/ComponentListContainer";
import { UserAccessContext } from "../UserAccessContext";
import { RELEVANT_FIELDS_BY_OBJECT_URL_NAME } from "./ComponentsTabs.constants";
import { GeneratorTab } from "./NonWizardTabs/GeneratorTab";
import { WeightingTab } from "./NonWizardTabs/WeightingTab";

type TabProps = TabsTabProps & { label: string };

interface ComponentTabsProps {
  siteId: number;
  variantId: number;
  projectId: string;
  graphCoordinates: { x: number; y: number };
  setShowNotAccessibleModal: (show: boolean) => void;
}

function ComponentTabs({
  siteId,
  variantId,
  projectId,
  graphCoordinates,
  setShowNotAccessibleModal
}: ComponentTabsProps) {
  const navigate = useNavigate();
  const resolved = useResolvedPath(":objectUrlName/*");
  const match = useMatch({ path: resolved.pathname });
  const activeTab = match
    ? match.params.objectUrlName
    : ObjectUrlName.Generators;

  const userCanCreateAndDelete = useContext(UserAccessContext);

  const nonWizardTabs = {
    [ObjectName.Weighting]: <WeightingTab siteId={siteId} />,
    [ObjectName.Generator]: (
      <GeneratorTab
        graphCoordinates={graphCoordinates}
        projectId={projectId}
        setShowNotAccessibleModal={setShowNotAccessibleModal}
        siteId={siteId}
        userHasAccess={
          !!userCanCreateAndDelete &&
          userCanCreateAndDelete[ObjectName.Generator]
        }
        variantId={variantId}
      />
    )
  } as const;

  const tabs = Object.values(ObjectName)
    .filter((objectName) => objectName in TABLE_IDS)
    .map(
      (objectName) =>
        ({
          value: OBJECT_NAMES_TO_OBJECT_URL_NAMES[objectName],
          label: objectName
        }) as TabProps
    );

  const { missingFields } = useMissingData();
  function areRelevantFieldsMissing(objectUrlName: string) {
    const relevantFields = RELEVANT_FIELDS_BY_OBJECT_URL_NAME[objectUrlName];
    if (!relevantFields || !missingFields) return false;

    return relevantFields.some((field) => missingFields.includes(field));
  }

  return (
    <div className="ComponentTabs">
      <Tabs
        value={activeTab ?? ObjectUrlName.Generators}
        onChange={(newTab) =>
          navigate(`../${projectId}/liegenschaften/${siteId}/${newTab}`)
        }
      >
        <Tabs.List grow>
          {tabs.map((tab) => (
            <TabWithHighlightAndError
              component="a"
              highlighted={areRelevantFieldsMissing(tab.value)}
              key={tab.value}
              value={tab.value}
            >
              {tab.label}
            </TabWithHighlightAndError>
          ))}
        </Tabs.List>

        {tabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value}>
            {nonWizardTabs[tab.label] ? (
              nonWizardTabs[tab.label]
            ) : (
              <ComponentListContainer
                graphCoordinates={graphCoordinates}
                objectDisplayName={tab.label}
                objectName={OBJECT_URL_NAMES_TO_OBJECT_NAMES[tab.value]}
                projectId={projectId}
                setShowNotAccessibleModal={setShowNotAccessibleModal}
                siteId={siteId}
                userHasAccess={
                  !!userCanCreateAndDelete &&
                  userCanCreateAndDelete[
                    OBJECT_URL_NAMES_TO_OBJECT_NAMES[tab.value]
                  ]
                }
                variantId={variantId}
              />
            )}
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
}

export { ComponentTabs, ComponentTabsProps };
